/** Care Attendant CareHub — packages, 2-hour slots, marketplace defaults. */

import { parseNpidParts } from "../utils/npidClient";
import { CARE_ATTENDANT_HOURLY_PACKAGES } from "./nemaHourlyServiceList";

export const ATTENDANT_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const ATTENDANT_MAX_DAILY_HOURS_DEFAULT = 8;
export const ATTENDANT_MAX_DAILY_HOURS_MAX = 16;
export const ATTENDANT_MIN_REST_HOURS_DEFAULT = 2;
export const ATTENDANT_MIN_REST_HOURS_MAX = 8;
export const ATTENDANT_MAX_BOOKINGS_DEFAULT = 3;
export const ATTENDANT_MAX_BOOKINGS_MAX = 8;
export const ATTENDANT_SERVICE_RADIUS_DEFAULT_KM = 15;
export const ATTENDANT_SERVICE_RADIUS_MAX_KM = 25;
export const ATTENDANT_SERVICE_RADIUS_MIN_KM = 1;
export const ATTENDANT_TRAVEL_BUFFER_DISPLAY_MIN = 20;

export const ATTENDANT_LEAVE_REASON = "LEAVE";
export const ATTENDANT_BLOCK_REASON = "BLOCK";

/** Fixed 2-hour booking windows for attendant weekly roster. */
export { CAREHUB_PREDEFINED_SLOTS as ATTENDANT_PREDEFINED_SLOTS } from "./careHubRosterConfig";
export { CAREHUB_DEFAULT_DAYTIME_SLOT_IDS as ATTENDANT_DEFAULT_SLOT_IDS } from "./careHubRosterConfig";

/**
 * Care Attendant service packs shown on Availability & Pricing.
 * Prices from NEMA hourly service list draft (see nemaHourlyServiceList.js).
 */
export const ATTENDANT_PACKAGES = CARE_ATTENDANT_HOURLY_PACKAGES.map((pack) => ({
  id: pack.id,
  title: pack.title,
  durationLabel: pack.durationLabel,
  tasks: [...(pack.tasks || [])],
  pricingMode: pack.pricingMode,
  defaultPriceInr: pack.defaultPriceInr,
  tiers: pack.tiers
    ? pack.tiers.map((t) => ({
        id: t.id,
        label: t.label,
        defaultPriceInr: t.defaultPriceInr,
      }))
    : undefined,
}));

export function createEmptyAttendantPackageMap() {
  const map = {};
  for (const pack of ATTENDANT_PACKAGES) {
    if (pack.pricingMode === "tiers") {
      const tierPrices = {};
      for (const t of pack.tiers) {
        tierPrices[t.id] = t.defaultPriceInr;
      }
      map[pack.id] = { offered: true, tierPrices };
    } else {
      map[pack.id] = { offered: true, priceInr: pack.defaultPriceInr };
    }
  }
  return map;
}

export function mergeAttendantPackageMap(raw) {
  const base = createEmptyAttendantPackageMap();
  if (!raw || typeof raw !== "object") return base;
  const next = { ...base };
  for (const pack of ATTENDANT_PACKAGES) {
    const row = raw[pack.id];
    if (!row || typeof row !== "object") continue;
    if (pack.pricingMode === "tiers") {
      const tierPrices = { ...base[pack.id].tierPrices };
      for (const t of pack.tiers) {
        const n = Number(row.tierPrices?.[t.id] ?? row[`price_${t.id}`]);
        if (Number.isFinite(n) && n > 0) tierPrices[t.id] = Math.round(n);
      }
      next[pack.id] = { offered: row.offered !== false, tierPrices };
    } else {
      const n = Number(row.priceInr);
      next[pack.id] = {
        offered: row.offered !== false,
        priceInr: Number.isFinite(n) && n > 0 ? Math.round(n) : pack.defaultPriceInr,
      };
    }
  }
  return next;
}

export function createEmptyAttendantHomeBase() {
  return {
    houseNo: "",
    street: "",
    locality: "",
    city: "",
    state: "",
    pincode: "",
  };
}

export function mergeAttendantHomeBase(raw) {
  const base = createEmptyAttendantHomeBase();
  if (!raw || typeof raw !== "object") return base;
  const out = {
    ...base,
    houseNo: String(raw.houseNo || raw.doorNo || "").trim(),
    street: String(raw.street || "").trim(),
    locality: String(raw.locality || raw.place || raw.area || "").trim(),
    city: String(raw.city || "").trim(),
    state: String(raw.state || "").trim(),
    pincode: String(raw.pincode || "").trim(),
  };
  /* Preserve GPS coordinates if present */
  if (raw.latitude != null) out.latitude = raw.latitude;
  if (raw.longitude != null) out.longitude = raw.longitude;
  return out;
}

function appendUniquePart(parts, value) {
  const v = String(value || "").trim();
  if (!v) return;
  const hay = parts.join(", ").toLowerCase();
  if (!hay.includes(v.toLowerCase())) parts.push(v);
}

/** Split a saved one-line address into structured home-base fields. */
export function parseAddressLineIntoHomeBase(line, { city = "", state = "", pincode = "" } = {}) {
  const text = String(line || "").trim();
  if (!text) return {};

  const segments = text.split(",").map((s) => s.trim()).filter(Boolean);
  if (!segments.length) return {};

  const parts = [...segments];
  const pin = String(pincode || "").trim();
  const cityPart = String(city || "").trim();
  const statePart = String(state || "").trim();

  const dropTail = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (!v) return;
    const last = parts[parts.length - 1];
    if (last && last.toLowerCase() === v) parts.pop();
  };

  if (pin && /^\d{6}$/.test(pin)) dropTail(pin);
  dropTail(statePart);
  dropTail(cityPart);

  if (parts.length >= 3) {
    return {
      houseNo: parts[0],
      street: parts[1],
      locality: parts.slice(2).join(", "),
    };
  }
  if (parts.length === 2) {
    const first = parts[0];
    if (/^\d/.test(first)) {
      return { houseNo: first, street: parts[1] };
    }
    return { street: first, locality: parts[1] };
  }
  if (parts.length === 1) {
    const only = parts[0];
    if (/^\d{6}$/.test(only)) return { pincode: only };
    if (/^\d/.test(only)) return { houseNo: only };
    return { locality: only };
  }
  return {};
}

export function isSparseHomeBase(homeBase) {
  const structured = mergeAttendantHomeBase(homeBase);
  return !structured.houseNo && !structured.street && !structured.locality;
}

/** Best label for view mode when structured home base may only have pincode / region. */
export function resolveHomeBaseDisplayAddress(homeBase, fallbacks = {}) {
  const merged = mergeAttendantHomeBase(homeBase);
  const structured = formatAttendantHomeAddress(merged);

  if (!isSparseHomeBase(merged)) return structured;

  const fallbackAddress = String(fallbacks.address || fallbacks.location || "").trim();
  if (fallbackAddress) {
    const parsed = mergeAttendantHomeBase({
      ...parseAddressLineIntoHomeBase(fallbackAddress, {
        city: fallbacks.city || merged.city,
        state: fallbacks.state || merged.state,
        pincode: fallbacks.pincode || merged.pincode,
      }),
      city: fallbacks.city || merged.city,
      state: fallbacks.state || merged.state,
      pincode: fallbacks.pincode || merged.pincode,
    });
    const fromProfile = formatAttendantHomeAddress(parsed);
    if (fromProfile.length > structured.length) return fromProfile;
    if (fallbackAddress.length > structured.length) return fallbackAddress;
  }

  return (
    formatAttendantHomeAddress({
      ...merged,
      city: merged.city || fallbacks.city,
      state: merged.state || fallbacks.state,
      pincode: merged.pincode || fallbacks.pincode,
    }) || structured
  );
}

/** Build a single-line home base label from structured or legacy profile fields. */
export function formatAttendantHomeAddress(input = {}) {
  const structured = mergeAttendantHomeBase(input);
  const hasStructured = Boolean(
    structured.houseNo ||
      structured.street ||
      structured.locality ||
      structured.city ||
      structured.state ||
      structured.pincode
  );

  if (hasStructured) {
    const parts = [];
    appendUniquePart(parts, structured.houseNo);
    appendUniquePart(parts, structured.street);
    appendUniquePart(parts, structured.locality);
    appendUniquePart(parts, structured.city);
    appendUniquePart(parts, structured.state);
    appendUniquePart(parts, structured.pincode);
    return parts.join(", ");
  }

  const parts = [];
  const primary = String(input.address || input.location || "").trim();
  const cityPart = String(input.city || "").trim();
  const statePart = String(input.state || "").trim();
  const pin = String(input.pincode || "").trim();

  if (primary) parts.push(primary);
  appendUniquePart(parts, cityPart);
  appendUniquePart(parts, statePart);
  appendUniquePart(parts, pin);
  return parts.join(", ");
}

/** Hydrate structured home base from profile + saved attendantCareHub block. */
export function resolveAttendantHomeBase({
  savedHomeBase,
  address,
  location,
  city,
  state,
  pincode,
  latitude,
  longitude,
} = {}) {
  const merged = mergeAttendantHomeBase(savedHomeBase);
  const cityPart = String(city || merged.city || "").trim();
  const statePart = String(state || merged.state || "").trim();
  const pin = String(pincode || merged.pincode || "").trim();
  const line = String(address || location || "").trim();

  // Split combined values like "Bengaluru, Karnataka" stored in city field.
  if (cityPart.includes(",") && !statePart) {
    const [cityName, stateName] = cityPart.split(",").map((s) => s.trim());
    if (cityName) merged.city = merged.city || cityName;
    if (stateName) merged.state = merged.state || stateName;
  } else {
    if (!merged.city && cityPart) merged.city = cityPart;
    if (!merged.state && statePart) merged.state = statePart;
  }
  if (!merged.pincode && pin) merged.pincode = pin;

  const hasLineFields = Boolean(merged.houseNo || merged.street || merged.locality);
  if (!hasLineFields && line) {
    const parsed = parseAddressLineIntoHomeBase(line, {
      city: merged.city || cityPart,
      state: merged.state || statePart,
      pincode: merged.pincode || pin,
    });
    if (parsed.houseNo) merged.houseNo = parsed.houseNo;
    if (parsed.street) merged.street = parsed.street;
    if (parsed.locality) merged.locality = parsed.locality;
    if (parsed.pincode) merged.pincode = parsed.pincode;

    if (!merged.houseNo && !merged.street && !merged.locality) {
      const lower = line.toLowerCase();
      const cityLower = cityPart.toLowerCase();
      const stateLower = statePart.toLowerCase();
      if (
        line &&
        !(cityLower && stateLower && lower === `${cityLower}, ${stateLower}`) &&
        !(cityLower && lower === cityLower)
      ) {
        merged.locality = line;
      }
    }
  }

  /* Carry GPS coordinates from the profile row into the homeBase object */
  if (merged.latitude == null && latitude != null) merged.latitude = latitude;
  if (merged.longitude == null && longitude != null) merged.longitude = longitude;

  return merged;
}

/** Show issued NCID; no client-side INC minting (INC is not issued in this app). */
export function formatAttendantProviderId(npid) {
  const raw =
    typeof npid === "object" && npid != null
      ? npid.code || npid.npid || npid.value || ""
      : npid;
  const code = String(raw || "").trim().toUpperCase();
  if (code && code !== "[OBJECT OBJECT]") {
    if (parseNpidParts(code)) return code;
    if (code.startsWith("INC-")) return code;
  }
  return "NCID pending verification";
}
